<template>
  <div class="container">
    <div>
      <div v-for="(item, index) in manifestList" v-bind:key="item">
        <div class="manifestItem" @click="handleClick(item, index)" :class="{ 'selected': selectedIndex === index }">
          <div class="itemBox">
            <div class="data">
              <div class="label">Specimen Collected</div>{{ formatDate(item?.date) }}
            </div>
            <div class="data">
              <div class="label">Specimen Type</div>
              <span class="bold">{{ item.specimenType ?? '—' }}</span>
            </div>
            <div class="data" v-if="item.thumbnail">
              <div class="label">Test Type</div>{{ item.testType || '—' }}
            </div>
            <div class="location">
              <span class="material-symbols-outlined size-24 location-icon">
                location_on
              </span>
              <span class="location-text">{{ item.labName || '—' }}</span>
            </div>
          </div>
          <div v-if="item.mimeType" class="report-only">
            <span>{{ item.mimeType.includes('pdf') ? 'PDF' : 'TXT' }}</span>
          </div>
          <img v-if="item.thumbnail?.url" v-bind:src="item.thumbnail.url" class="thumbImage" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineEmits, defineExpose, onMounted, ref, toRaw, watch } from 'vue'
import { useStore } from 'vuex'
import { getPathology } from './getPathology.js'
import { clearCenterColumn, closeCenterColumn } from '@/ux3/components/CenterColumnManagement/centerColumnManagement.js'
import loadThumbnails from './loadThumbnails.js'
import { formatDate } from '@/utils/utils'
import { getReportFile } from '@/ux3/cards/ReportViewer/getReportFile.js'

const store = useStore()

const emit = defineEmits(['onManifestCountChange'])

const patientId = computed(() => store.getters.session?.patient?.id || '')

const manifestList = ref([])
const isLocked = ref(false)
const selectedIndex = ref(-1)

const handleClick = async (item, index) => {
  if (isLocked.value) { return }

  selectedIndex.value = index

  // if clicked item is a report
  if (item.id && item.mimeType) {
    store.dispatch('showViewer', true)
    store.dispatch('reportDocType', item.mimeType)
    store.dispatch('reportDocument', await getReportFile(item.id, item.mimeType))
    closeCenterColumn()
    return
  }

  // if clicked item is a specimen
  isLocked.value = true
  store.commit('setSelectedPathologyObject', {
    id: item.manifestId,
    accessionNumber: item.accessionNumber,
  })
  clearCenterColumn('pathology')

  // prevent user from spamming clicks and sending unnecessary api requests
  setTimeout(() => {
    isLocked.value = false
  }, 1000)
}


const loadSamples = async (id) => {
  if (!id) return
  const results = await getPathology(id)
  manifestList.value = await loadThumbnails(toRaw(results)) || []
  emit('onManifestCountChange', manifestList.value.length)
}

watch(patientId, async () => {
  await loadSamples(patientId.value)
})

onMounted(async () => {
  await loadSamples(patientId.value)
})

defineExpose({ loadSamples })
</script>

<style lang="scss" scoped>
.bold {
  font-weight: bold;
}

.thumbImage {
  max-width: 100px;
  max-height: 90px;
  margin: 20px;
  border-radius: 10px;
}


.data {
  color: var(--Black);
  margin-bottom: 15px;
}

.itemBox {
  margin: 1rem;
}

.manifestItem {
  display: flex;
  justify-content: space-between;
  text-align: left;
  border-radius: 10px;
  transition: all linear 200ms;
  background-color: var(--White);
  overflow: hidden;
  position: relative;
  text-align: left;
  margin: 0.75rem;
  padding: 0.75rem;
  font-size: 0.8rem;

  &:hover {
    cursor: pointer;
    box-shadow: 5px 5px 5px var(--Grey500);
    transition: all ease-out 200ms;
  }

  &.selected {
    box-shadow: 3px 3px 10px var(--Grey800);
    transition: all ease-out 200ms;
  }

  .report-only {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--Grey700);
    color: var(--White);
    position: absolute;
    border-radius: 5px;
    font-size: 70%;
    top: 1rem;
    right: 2rem;
    height: 20px;
    width: 35px;
  }
}

.location {
  color: var(--Grey500);
  display: flex;
  flex-direction: row;
  font-size: 90%;
}

.location-icon {
  padding-bottom: 3px;
}

.location-text {
  padding-top: 3px;
}
</style>
